'use client'

import React, { FC, ReactElement } from 'react'
import { ThemeToggleIcon } from '@/components/ui/theme-toggle'
import HeaderDrawer from '@/components/header-drawer'
import HeaderRight from '@/components/header-right'
import Body from '@/components/body'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import SearchInput from '@/components/search-input'
import NavigationLinks from './nav'
import NotificationsBell from '@/components/notifications-bell'
import CashbackBar, { CASHBACK_BAR_HEIGHT, useCashbackBar } from '@/components/offers/cashback-bar'
import SearchIcon2 from '@/icons/search-icon'

const LINKS = [
	{ name: 'Create', href: '/creator-dashboard' },
	{ name: 'Wallet', href: '/wallet' },
	{ name: 'Collect', href: '/collect' },
	{ name: 'Offers', href: '/offers' },
	{ name: 'Inscribe', href: '/inscribe' }
]

const Header: FC = (_props): ReactElement => {
	const loggedIn = useLoggedIn()
	const cashback = useCashbackBar()
	const headerHeight = 56 + (cashback.visible ? CASHBACK_BAR_HEIGHT : 0)

	// Panels pinned under the header (activity drawer, bids tabs, bitmap)
	// read this instead of assuming 56px, so the cashback bar never hides
	// their top edge.
	React.useEffect(() => {
		document.documentElement.style.setProperty('--ow-header-h', `${headerHeight}px`)
	}, [headerHeight])

	const [scrolled, setScrolled] = React.useState(false)

	React.useEffect(() => {
		const listenScrollEvent = () => {
			setScrolled(window.scrollY > 20)
		}

		window.addEventListener('scroll', listenScrollEvent, { passive: true })
		return () => {
			window.removeEventListener('scroll', listenScrollEvent)
		}
	}, [])

	return (
		<>
			{/* Keep mobile chrome opaque and unanimated during Safari refresh/compositing. */}
			<header
				style={{ height: headerHeight }}
				className={`fixed w-full z-[1000] flex flex-col  xl:backdrop-blur-[5px] border-b border-border bg-chrome-muted dark:bg-background ${
					scrolled ? 'xl:bg-chrome-muted/80 xl:dark:bg-background/60' : ''
				}`}
			>
				{cashback.visible && <CashbackBar />}
				<Body className="bg-transparent h-[56px] xl:!px-10">
					<div className="h-full flex items-center gap-4 hidden xl:flex">
						<NavigationLinks />

						<div className="relative min-w-0 flex-1">
							<SearchInput
								inputClassName="pl-8 w-full h-[32px] text-sm"
								inputRootClassName="w-full h-[32px]"
								enableDropdown={true}
								startAdornment={<SearchIcon2 className="size-3" />}
								startAdornmentClassName="left-2 absolute bg-transparent z-[10] top-1/2 transform -translate-y-1/2"
							/>
						</div>
						<HeaderRight />
					</div>
					<div className="xl:hidden h-full flex items-center gap-2">
						<NavigationLinks />
						<div className="relative min-w-0 flex-1">
							<SearchInput
								inputClassName="pl-8 w-full h-[32px] text-sm"
								inputRootClassName="w-full h-[32px] flex-grow"
								enableDropdown={true}
								startAdornment={<SearchIcon2 className="size-3" />}
								startAdornmentClassName="left-2 absolute bg-transparent z-[10] top-1/2 transform -translate-y-1/2"
							/>
						</div>
						{loggedIn && <NotificationsBell />}
						<div className="flex shrink-0 items-center gap-0.5">
							<ThemeToggleIcon />
						</div>
						<HeaderDrawer links={LINKS} />
					</div>
				</Body>
			</header>
			<div style={{ height: headerHeight }} /> {/* Bumper */}
		</>
	)
}

export default Header
